import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';

/** Global date-range and symbol filters (mandate §6) — the model behind the header's two chips, which the
 *  design drew and left inert.
 *
 *  THE SEMANTICS, stated once so no chip over-claims:
 *
 *  · The RANGE preset broadcasts to every widget whose own chips speak the same day/week/month/year/all
 *    vocabulary: the dashboard's equity curve (`eqRange`), goals (`gRange`), monthly calendar (`mcRange`),
 *    analytics (`anRange`), the score window (`pfRange`) and the Trades list (`tradeRange`). Widget chips stay
 *    live afterwards as LOCAL overrides — the global filter is a broadcast preset, not a lock — and structural
 *    ranges (the P&L calendar's month grid, the heatmap's quarter) are deliberately not touched, because "week"
 *    is not a shape a month grid can take.
 *
 *  · The SYMBOL filter is SINGLE-select with search, deliberately: every consumer (`anSym`, the journal filter
 *    `jfSym`) accepts one symbol or all, and a multi-select the consumers cannot honour would be a lying
 *    control. Choosing a symbol filters analytics and the journal; 'all' clears.
 *
 *  · Both persist across navigation (model state) for the session; a reload returns to defaults. The chip
 *    labels always SHOW the active filter, so an applied filter is visible and removable from the same place.
 */

export const G_RANGES = [
  ['day', 'Today'],
  ['week', 'This Week'],
  ['month', 'This Month'],
  ['year', 'This Year'],
  ['all', 'All Time'],
] as const;

/** The broadcast: one patch, every consumer, so the widgets cannot disagree about what was just applied. */
export function rangeBroadcast(range: 'day' | 'week' | 'month' | 'year' | 'all'): Record<string, string> {
  return {
    gFilterRange: range,
    eqRange: range,
    gRange: range,
    mcRange: range,
    anRange: range,
    pfRange: range,
    tradeRange: range,
  };
}

export function buildGlobalFilters(c: Ctx) {
  const S = c.S;
  const symbols = ['all', ...new Set(c.data.core.exTrades.map((t) => String(t[0])))];
  const query = S.gFilterSymQuery.trim().toUpperCase();
  const symbolChoices = symbols.filter((s) => s === 'all' || !query || s.toUpperCase().includes(query));

  return {
    gFilterOpen: S.gFilterOpen,
    gFilterToggle: (which: 'range' | 'symbols') =>
      c.set((st) => ({ gFilterOpen: st.gFilterOpen === which ? null : which, gFilterSymQuery: '' })),
    gFilterClose: () => c.set({ gFilterOpen: null }),
    gRangeLabel: G_RANGES.find(([id]) => id === S.gFilterRange)?.[1] ?? 'All Time',
    gRangeActive: S.gFilterRange !== 'all',
    gRangeChoices: G_RANGES.map(([id, label]) => ({
      id,
      label,
      on: S.gFilterRange === id,
      go: () => {
        c.set({ ...rangeBroadcast(id), gFilterOpen: null });
        c.flash(
          id === 'all'
            ? 'Range reset — every widget back on its own default.'
            : `${label} applied across the dashboard, analytics, score and trades.`,
          'mut',
        );
      },
    })),
    gSymLabel: S.gFilterSym === 'all' ? 'Symbols' : S.gFilterSym,
    gSymActive: S.gFilterSym !== 'all',
    gFilterSymQuery: S.gFilterSymQuery,
    gFilterSymSetQuery: (v: string) => c.set({ gFilterSymQuery: v }),
    gSymChoices: symbolChoices.map((sym) => ({
      id: sym,
      label: sym === 'all' ? 'All symbols' : sym,
      on: S.gFilterSym === sym,
      go: () => {
        c.set({ gFilterSym: sym, anSym: sym, jfSym: sym === 'all' ? '' : sym, gFilterOpen: null });
        c.flash(
          sym === 'all' ? 'Symbol filter cleared.' : `Filtering analytics and journal to ${sym}.`,
          'mut',
        );
      },
    })),
    gFilterReset: () => {
      c.set({ ...rangeBroadcast('all'), gFilterSym: 'all', anSym: 'all', jfSym: '', gFilterOpen: null });
      c.flash('Global filters cleared.', 'mut');
    },
  } satisfies Partial<ViewModel>;
}
